import { useRef } from 'react';

import { FileUp } from 'lucide-react';

import { fileToBase64 } from '@/lib/policySourceDocument';
import { showPromiseToast } from '@/lib/toast';

import { Button } from '@/components/ui/button';

import { useConvertPolicyDocxMutation } from '@/store/api/policyApi';

export interface ConvertedDocx {
  html: string;
  filename: string;
  base64: string;
}

interface DocxUploadButtonProps {
  onConverted: (result: ConvertedDocx) => void;
  disabled?: boolean;
}

/**
 * Upload a Word (.docx) document and convert it to HTML on the backend,
 * preserving headings, lists, tables, hyperlinks and inline images.
 */
const DocxUploadButton = ({ onConverted, disabled }: DocxUploadButtonProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [convertDocx, { isLoading }] = useConvertPolicyDocxMutation();

  const handleFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset so selecting the same file again re-triggers onChange
    event.target.value = '';

    if (!file) return;

    const conversion = async (): Promise<ConvertedDocx> => {
      const [response, base64] = await Promise.all([
        convertDocx(file).unwrap(),
        fileToBase64(file),
      ]);
      return { html: response.html, filename: response.filename, base64 };
    };

    try {
      const result = await showPromiseToast(conversion(), {
        loading: `Converting "${file.name}"...`,
        success: (converted) => `Imported "${converted.filename}"`,
        error: (error: any) =>
          error?.data?.detail || error?.message || 'Failed to convert document',
      });
      onConverted(result);
    } catch (error) {
      // Error is handled by toast
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type='file'
        accept='.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        className='hidden'
        onChange={handleFileSelected}
      />
      <Button
        type='button'
        variant='outline'
        size='sm'
        onClick={() => inputRef.current?.click()}
        disabled={disabled || isLoading}
        className='flex items-center gap-2'
      >
        <FileUp className='h-4 w-4' />
        {isLoading ? 'Converting...' : 'Import Word document'}
      </Button>
    </>
  );
};

export default DocxUploadButton;
